import { useEditor, EditorContent, BubbleMenu } from "@tiptap/react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import type { Editor } from "@tiptap/core";
import { Extension } from "@tiptap/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { Document } from "@tiptap/extension-document";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Text } from "@tiptap/extension-text";
import { History } from "@tiptap/extension-history";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { useEffect } from "react";
import Button from "../../../../../ui/Button/Button";
import {
  PatternSymbolColor,
  TemplateNodeType,
} from "../../../../../__generated__/gql/graphql";
import SymbolNode from "../../../Symbol/SymbolNode";
import styles from "./PatternTemplate.module.scss";
import Feedback from "../../../../../ui/Feedback/Feedback";
import { SymbolColor } from "../../../../../ui/Symbol/Symbol";
import symbolColors from "../../../../../ui/Symbol/symbol-colors";

const NoNewLine = Extension.create({
  name: "no_new_line",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("eventHandler"),
        props: {
          handleKeyDown: (_view, event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              return true;
            }

            if (event.key === "Backspace" && event.metaKey) {
              return true;
            }

            return false;
          },
        },
      }),
    ];
  },
});

function getRandomColor(usedColors: SymbolColor[]): SymbolColor {
  const availableColors = symbolColors.filter(
    (color) => !usedColors.includes(color) && color !== "gray",
  );

  return (
    availableColors[Math.floor(Math.random() * availableColors.length)] ??
    "purple"
  );
}

function mapPatternColor(color?: string): PatternSymbolColor {
  switch (color?.toLowerCase()) {
    case "purple":
      return PatternSymbolColor.Purple;
    case "green":
      return PatternSymbolColor.Green;
    case "blue":
      return PatternSymbolColor.Blue;
    case "cyan":
      return PatternSymbolColor.Cyan;
    default:
      return PatternSymbolColor.Green;
  }
}

const schema = z.object({
  symbols: z
    .object({
      color: z.nativeEnum(PatternSymbolColor),
      name: z.string().min(1, { message: "Required" }),
      values: z.string().array(),
    })
    .array()
    .min(1, { message: "Required" })
    .max(4, { message: "Maximum of 4 symbols allowed" }),

  templateNodes: z
    .object({
      type: z.nativeEnum(TemplateNodeType),
      value: z.string().min(1, { message: "Required" }),
    })
    .array()
    .min(1, { message: "Required" }),
});

export type PatternTemplateData = z.infer<typeof schema>;

function parseEditorContent(editor: Editor): PatternTemplateData {
  const json = editor.getJSON();
  const nodes = json.content?.[0].content;

  if (!nodes?.length) {
    return { symbols: [], templateNodes: [] };
  }

  const symbols: PatternTemplateData["symbols"] = nodes
    .filter((node) => node.type === "symbol")
    .map((node) => ({
      color: mapPatternColor(
        typeof node.attrs?.color === "string" ? node.attrs.color : undefined,
      ),
      name: node.content?.[0].text ?? "",
      values: [],
    }));

  const templateNodes: PatternTemplateData["templateNodes"] = nodes.map(
    (node) => {
      if (node.type === "symbol") {
        return {
          type: TemplateNodeType.Symbol,
          value: node.content?.[0].text ?? "",
        };
      }

      return { type: TemplateNodeType.Text, value: node.text ?? "" };
    },
  );

  return { symbols, templateNodes };
}

function PatternTemplate({
  onSubmit,
  defaultValue,
}: {
  onSubmit: (data: PatternTemplateData) => void;
  defaultValue: PatternTemplateData;
}) {
  const {
    handleSubmit,
    setValue,
    clearErrors,
    getValues,
    watch,
    formState: { errors },
  } = useForm<PatternTemplateData>({
    resolver: zodResolver(schema),
    defaultValues: defaultValue,
  });

  const symbols = watch("symbols");

  const editor = useEditor({
    extensions: [Document, Paragraph, Text, History, NoNewLine, SymbolNode],
    content: defaultValue.templateNodes.length
      ? {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: defaultValue.templateNodes.map((node) => {
                if (node.type === TemplateNodeType.Text) {
                  return {
                    type: "text",
                    text: node.value,
                  };
                }

                return {
                  type: "symbol",
                  attrs: {
                    color: (
                      defaultValue.symbols.find(
                        (symbol) => symbol.name === node.value,
                      )?.color ?? PatternSymbolColor.Purple
                    ).toLowerCase(),
                  },
                  content: [
                    {
                      type: "text",
                      text: node.value,
                    },
                  ],
                };
              }),
            },
          ],
        }
      : undefined,
    onUpdate: ({ editor: e }) => {
      const { templateNodes, symbols } = parseEditorContent(e);
      clearErrors();
      setValue("templateNodes", templateNodes);
      setValue("symbols", symbols);
    },
    onCreate: ({ editor: e }) => {
      const { templateNodes, symbols } = parseEditorContent(e);
      clearErrors();
      setValue("templateNodes", templateNodes);
      setValue("symbols", symbols);
    },
  });

  useEffect(() => {
    if (editor) {
      const { templateNodes, symbols } = parseEditorContent(editor);
      clearErrors();
      setValue("templateNodes", templateNodes);
      setValue("symbols", symbols);
    }
  }, [clearErrors, editor, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {editor ? (
        <BubbleMenu
          editor={editor}
          shouldShow={({ editor: innerEditor }) => {
            if (
              innerEditor.view.state.selection.$to.node(
                innerEditor.view.state.selection.$to.depth,
              ).type.name === "symbol" ||
              innerEditor.view.state.selection.$from.node(
                innerEditor.view.state.selection.$from.depth,
              ).type.name === "symbol"
            ) {
              return false;
            }

            const slice = innerEditor.view.state.selection.content();

            let symbolInSelection = false;

            slice.content.forEach((node) => {
              node.forEach((innerNode) => {
                if (innerNode.type.name === "symbol") {
                  symbolInSelection = true;
                }
              });
            });

            return !symbolInSelection;
          }}
        >
          {symbols.length < 4 && (
            <button
              className={styles.addSymbolButton}
              onClick={() => {
                const { view, state } = editor;

                let count = 0;

                view.state.doc.child(0).forEach((node) => {
                  if (node.type.name === "symbol") {
                    count += 1;
                  }
                });
                const { from, to } = view.state.selection;
                const selectedText = state.doc.textBetween(from, to, "");
                const symbolText =
                  selectedText.length > 0
                    ? selectedText
                    : `Symbol ${count + 1}`;

                const { nodeAfter } = editor.view.state.selection.$to;

                const usedColors = getValues("symbols").map(
                  (symbol) => symbol.color.toLowerCase() as SymbolColor,
                );

                let commands = editor
                  .chain()
                  .focus()
                  .insertContent([
                    {
                      type: "symbol",
                      attrs: {
                        color: getRandomColor(usedColors ?? []),
                      },
                      content: [
                        {
                          type: "text",
                          text: symbolText,
                        },
                      ],
                    },
                    ...(!nodeAfter?.text?.startsWith(" ")
                      ? [
                          {
                            type: "text",
                            text: " ",
                          },
                        ]
                      : []),
                  ]);

                if (!selectedText.length) {
                  commands = commands.setTextSelection({
                    from: editor.view.state.selection.$anchor.pos + 1,
                    to:
                      editor.view.state.selection.$anchor.pos +
                      1 +
                      symbolText.length,
                  });
                }

                commands.run();
              }}
              type="button"
            >
              Add Symbol
            </button>
          )}
        </BubbleMenu>
      ) : null}
      <div className={styles.input}>
        <EditorContent editor={editor} />
      </div>
      {errors.symbols && (
        <Feedback
          className={styles.error}
        >{`Pattern must contain at least 1 symbol`}</Feedback>
      )}
      <Button className={styles.submitButton} type="submit" variant="primary">
        Continue
      </Button>
    </form>
  );
}

export default PatternTemplate;
